import React, { useMemo } from 'react';
import type { Client, Language, Order } from '../types';
import type { Dict } from '../shared/i18n';
import { LOCALES } from '../shared/i18n';
import { computeStats, startOfWeek, todayKey } from '../shared/domain';
import type { Entitlement } from '../shared/entitlements';
import { Card, Empty, StatusBanner } from '../components/shared';

interface DashboardProps {
  orders: Order[];
  clients: Client[];
  lang: Language;
  t: Dict;
  today: string;
  entitlement: Entitlement | null;
  onStartOrder: () => void;
  onUpgrade: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({
  orders, clients, lang, t, today, entitlement, onStartOrder, onUpgrade,
}) => {
  const stats = useMemo(() => {
    const todayOrders = orders.filter((o) => o.dateString === today);
    const weekStart = startOfWeek().getTime();
    const key = todayKey();
    const dueToday = clients.filter((c) => c.deliveryDays.includes(key));
    const served = new Set(todayOrders.map((o) => o.clientId));

    return {
      today: computeStats(todayOrders),
      week: computeStats(orders.filter((o) => o.timestamp >= weekStart)),
      dueToday: dueToday.length,
      remaining: dueToday.filter((c) => !served.has(c.id)).length,
    };
  }, [orders, clients, today]);

  const nf = new Intl.NumberFormat(lang === 'ar' ? 'ar-MA-u-nu-latn' : LOCALES[lang], {
    maximumFractionDigits: 0,
  });
  const timeFmt = new Intl.DateTimeFormat(lang === 'ar' ? 'ar-MA-u-nu-latn' : LOCALES[lang], {
    hour: '2-digit', minute: '2-digit', hour12: false,
  });

  return (
    <div className="space-y-4 qd-enter">
      <StatusBanner entitlement={entitlement} t={t} onUpgrade={onUpgrade} />

      <div className="grid grid-cols-2 gap-3">
        <Card>
          <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{t.todaySales}</p>
          <p className="text-xl font-extrabold text-[#C1272D] mt-1">
            {nf.format(stats.today.totalSales)} <span className="text-xs">{t.currency}</span>
          </p>
        </Card>
        <Card>
          <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{t.todayDeliveries}</p>
          <p className="text-xl font-extrabold text-[#006233] mt-1">{stats.today.orderCount}</p>
        </Card>
      </div>

      <button
        onClick={onStartOrder}
        className="w-full bg-[#C1272D] text-white py-5 rounded-2xl font-extrabold text-lg shadow-lg shadow-red-100 active:scale-[0.99] transition-transform flex items-center justify-center gap-3"
      >
        <span aria-hidden="true">📦</span>
        {t.startOrder}
      </button>

      {stats.dueToday > 0 && (
        <Card className="flex justify-between items-center">
          <div>
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{t.todayClients}</p>
            <p className="text-lg font-extrabold mt-0.5">
              {stats.remaining > 0 ? `${stats.remaining} / ${stats.dueToday}` : `${stats.dueToday} ✓`}
            </p>
          </div>
          {stats.remaining === 0 && <span className="text-2xl" aria-hidden="true">🎉</span>}
        </Card>
      )}

      {stats.week.outstanding > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex justify-between items-center">
          <span className="text-[10px] font-extrabold text-amber-700 uppercase tracking-widest">{t.outstanding}</span>
          <span className="font-extrabold text-amber-800">
            {nf.format(stats.week.outstanding)} {t.currency}
          </span>
        </div>
      )}

      <div className="space-y-3">
        <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-1">
          {t.recentDeliveries}
        </p>

        {orders.length === 0 ? (
          <Empty icon="📑" title={t.noOrders} hint={t.startOrder} />
        ) : (
          orders.slice(0, 6).map((order) => (
            <Card key={order.id} className="flex justify-between items-center">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-lg shrink-0" aria-hidden="true">🏪</span>
                <div className="min-w-0">
                  <p className="font-extrabold text-slate-900 text-sm truncate">{order.clientName}</p>
                  <p className="text-[10px] text-slate-400 font-bold">{timeFmt.format(order.timestamp)}</p>
                </div>
              </div>
              <p className="font-extrabold text-[#006233] text-sm shrink-0">
                {nf.format(order.totalAmount)} <span className="text-[9px]">{t.currency}</span>
              </p>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Dashboard;
